import {
    AuthConfiguration,
    TokenClaimDto,
    TokenResponseDto,
    ValidateSSOUserCodeEnum
} from '@tassat/ui-definitions';
import { browserSessionStore } from '@tassat/utility';
import axios, { AxiosResponse } from 'axios';
import jwt from 'jsonwebtoken';
import qs from 'qs';

interface ValidateUserResponse {
    responseCode: string;
    userId: string;
    isAdmin: string;
    isLocked: string;
}

type Config = {
    getAuthConfig: () => Promise<AuthConfiguration>;
    networkConfig: {
        adminIp: string;
        clientIp: string;
    };
    history: any;
    onPostAuthentication: () => unknown;
    onPostRefresh?: () => unknown;
    onPreLogout?: () => Promise<unknown> | unknown;
    clientRoutingUrl: string;
};

class AuthService {
    static TOKEN_RENEWAL_TOTAL_ATTEMPTS = 2;
    protected static getAuthConfig: () => Promise<AuthConfiguration>;
    protected static redirectUris: { client: string; admin: string };
    protected static history: any;
    private static onPostAuthentication: () => unknown;
    private static onPostRefresh: (() => unknown) | undefined;
    private static onPreLogout: (() => Promise<unknown> | unknown) | undefined;
    private static clientRoutingUrl: string;
    private static inFlightRefresh: Promise<TokenResponseDto> | null = null;
    private static sideEffectsFired: Set<unknown> = new Set();

    static async configurations(config: Config): Promise<void> {
        this.getAuthConfig = config.getAuthConfig;
        this.redirectUris = {
            client: `${config.networkConfig.clientIp}/callback`,
            admin: `${config.networkConfig.adminIp}/admin_callback`
        };
        this.history = config.history;
        this.onPostAuthentication = config.onPostAuthentication;
        this.onPostRefresh = config.onPostRefresh;
        this.onPreLogout = config.onPreLogout;
        this.clientRoutingUrl = config.clientRoutingUrl;
    }

    protected async validateUser(forAdmin: boolean, idToken: string): Promise<boolean | undefined> {
        let isAdmin: boolean;

        try {
            const userValidationRes: AxiosResponse<ValidateUserResponse> = await axios({
                method: 'get',
                url: `/validateUser?forAdmin=${forAdmin}`,
                headers: {
                    token: idToken,
                    sourceId: 'test'
                }
            });

            const validationCode = userValidationRes.data && userValidationRes.data.responseCode;
            const userId = userValidationRes?.data?.userId;
            isAdmin = userValidationRes?.data?.isAdmin === 'true';

            if (validationCode === ValidateSSOUserCodeEnum.invalidUser) {
                AuthService.history.replace('/invalid');

                return;
            } else if (validationCode === ValidateSSOUserCodeEnum.requestCreated) {
                AuthService.history.replace('/requestCreated', { isAdmin });

                return;
            } else if (validationCode === ValidateSSOUserCodeEnum.requestPending) {
                AuthService.history.replace('/pendingSetup', { isAdmin });

                return;
            } else if (validationCode === ValidateSSOUserCodeEnum.validUser) {
                // check is_admin and user_id is not undefined, if not then throw exception

                // set window session for userId and isAdmin
                browserSessionStore.setItem('userid', userId);
                browserSessionStore.setItem('is_admin', String(isAdmin));

                if (!userValidationRes || isAdmin === undefined || !userId) {
                    throw new Error(`Issue has been encountered with the validateUser request. Response was the following: ${userValidationRes}`);
                } else if (userValidationRes.data.isLocked === 'true') {
                    AuthService.history.replace('/locked');

                    return;
                }
            } else {
                // no valid authentication type found on the JWT
                console.warn('Invalid authentication');
                this.logout(isAdmin, idToken);

                return;
            }

            return isAdmin;
        } catch (error) {
            console.error('User validation failed', error);
            this.logout(forAdmin, idToken);

            return;
        }
    }

    protected redirectToApplication(isAdmin: boolean): void {
        // when we have a proper decoded jwt, get the claims, else we logout as a precaution
        if (isAdmin) {
            AuthService.history.replace('/admin');
        } else {
            AuthService.history.replace(AuthService.clientRoutingUrl);
        }
    }

    protected exponentialDelay(retryCount: number): number {
        const base = 2;
        const initialDelay = 1000;

        return Math.pow(base, retryCount) * initialDelay;
    }

    async isAuthenticated(
        refreshToken: (workerInstance: null) => Promise<void>
    ): Promise<boolean> {
        let isAuthenticated = false;

        // check if authenticated using the stored Access Token
        const accessToken = browserSessionStore.getItem('access_token');
        const idToken = browserSessionStore.getItem('id_token');
        if (accessToken && accessToken !== '' || idToken && idToken !== '') isAuthenticated = true;

        // if user reached site through history from another site or don't have navigator access to tell, they need to log in again
        if (!window.performance || (window.performance && window.performance?.navigation?.type === 2)) isAuthenticated = false;

        const isBrowserRefreshed = browserSessionStore.getItem('browser_refresh');
        const isRefresher = browserSessionStore.getItem('refresher');
        browserSessionStore.removeItem('browser_refresh');
        browserSessionStore.removeItem('refresher');

        if (isBrowserRefreshed === 'true' && isRefresher !== 'true') {
            // Check whether the current time is past the ID Token's expiry time
            const expiresAt = browserSessionStore.getItem('expires_at');
            const expiresAtCheck = Number(expiresAt) * 1000;

            isAuthenticated = new Date()
                .getTime() < expiresAtCheck;

            if (expiresAt && !isAuthenticated) {
                try {
                    // Refreshing the token
                    await refreshToken(null);

                    return true;
                } catch (error) {
                    console.error('Refresh token failed during page refresh - ', error);

                    return false;
                }
            }
        }

        if (isAuthenticated) {
            AuthService.onPostAuthentication();
        }

        return isAuthenticated;
    }

    async refreshToken(
        workerInstance: any,
        tokenUrl: string,
        tokenResponseMapper: (data: any) => TokenResponseDto,
        tokenClaimsMapper: (decoded: TokenClaimDto) => TokenClaimDto
    ): Promise<void> {
        // Overlapping callers (e.g. two near-simultaneous `TokenExpiration` WebSocket pushes, or a
        // WebSocket-triggered refresh racing a browser-reload refresh) must not both redeem the same
        // refresh_token - once Keycloak enforces single-use tokens, the loser of that race gets a hard
        // `invalid_grant` instead of a harmless duplicate success. Share one in-flight exchange across
        // all concurrent callers instead.
        if (!AuthService.inFlightRefresh) {
            AuthService.inFlightRefresh = this.exchangeRefreshToken(tokenUrl, tokenResponseMapper, tokenClaimsMapper)
                .finally(() => {
                    AuthService.inFlightRefresh = null;
                    AuthService.sideEffectsFired.clear();
                });
        }

        const tokenResponseDto = await AuthService.inFlightRefresh;

        // Each caller still needs its own side effect (push to its own worker vs. reload the browser) -
        // but two callers sharing the same `workerInstance` (e.g. duplicate TokenExpiration messages for
        // the same live socket) must only fire it once for this exchange.
        if (AuthService.sideEffectsFired.has(workerInstance)) {
            return;
        }
        AuthService.sideEffectsFired.add(workerInstance);

        if (workerInstance) {
            // On TokenExpiration, send TokenUpdate
            workerInstance.tokenUpdate(Buffer.from(tokenResponseDto.id_token)
                .toString('base64'));

            // Keep any server-side session artifacts (e.g. httpOnly cookies) in sync with the refreshed token
            AuthService.onPostRefresh?.();
        } else {
            // On browser refresh, reconnect to platform
            browserSessionStore.setItem('refresher', 'true');
            location.reload();
        }
    }

    private async exchangeRefreshToken(
        tokenUrl: string,
        tokenResponseMapper: (data: any) => TokenResponseDto,
        tokenClaimsMapper: (decoded: TokenClaimDto) => TokenClaimDto
    ): Promise<TokenResponseDto> {
        const forAdmin = browserSessionStore.getItem('is_admin') === 'true';
        let tokenRenewalAttempt = 0;
        const authConfig = await AuthService.getAuthConfig();
        const clientId = forAdmin ? authConfig.AUTH_ADMIN_ID : authConfig.AUTH_CLIENT_ID;
        const clientSecret = forAdmin ? authConfig.AUTH_ADMIN_SECRET : authConfig.AUTH_CLIENT_SECRET;

        while (tokenRenewalAttempt < AuthService.TOKEN_RENEWAL_TOTAL_ATTEMPTS) {
            ++tokenRenewalAttempt;

            if (tokenRenewalAttempt > 1) {
                const retryDelay = this.exponentialDelay(tokenRenewalAttempt);
                console.log(`Attempting to refresh tokens again in ${retryDelay / 1000} seconds`);
                const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
                await delay(retryDelay);
            }

            // NOTE: If in case the initial token call breaks in dev, not passing `audience` is the problem.
            // Re-read `refresh_token` on every attempt - a token already consumed by a prior attempt
            // (rotated server-side) must never be blindly resent on retry.
            const requestData = {
                grant_type: 'refresh_token',
                client_id: clientId,
                client_secret: clientSecret,
                refresh_token: browserSessionStore.getItem('refresh_token')
            };

            try {
                const response = await axios({
                    method: 'POST',
                    url: `${authConfig.AUTH_PROTOCOL}${authConfig.AUTH_CUSTOM_DOMAIN}${tokenUrl}`,
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    data: qs.stringify(requestData),
                    withCredentials: false
                });

                const tokenResponseDto = tokenResponseMapper(response.data);
                const decoded = jwt.decode(tokenResponseDto.id_token) as unknown as TokenClaimDto;
                const tokenClaimDto = tokenClaimsMapper(decoded);

                this.removeSessionStorage(false);
                this.setSessionTokens(tokenResponseDto, tokenClaimDto.exp);

                return tokenResponseDto;
            } catch (error: any) {
                console.error(`Attempt ${tokenRenewalAttempt}/${AuthService.TOKEN_RENEWAL_TOTAL_ATTEMPTS} to renew tokens failed - ${error}`);

                // An already-consumed/rotated refresh token will never succeed on retry - fail fast
                // instead of burning the remaining attempts on a guaranteed repeat failure.
                const isInvalidGrant = error?.response?.data?.error === 'invalid_grant';

                if (isInvalidGrant || tokenRenewalAttempt === AuthService.TOKEN_RENEWAL_TOTAL_ATTEMPTS) {
                    throw new Error(error);
                }
            }
        }

        // Unreachable in practice: the loop above always returns or throws before exiting.
        throw new Error('Token refresh failed: exhausted retry attempts without a definitive result.');
    }

    async logout(
        isAdmin: boolean = browserSessionStore.getItem('is_admin') === 'true',
        idToken?: string
    ): Promise<void> {
        try {
            await AuthService.onPreLogout?.();
        } catch (error) {
            console.error('onPreLogout hook failed during logout - proceeding with logout anyway', error);
        }

        // Logout must never fail to run - if `id_token`'s ciphertext is out of sync with the
        // currently-stored `expires_at` (e.g. corrupted storage, an unrelated bug), decrypting it
        // throws. Falling back to an empty string still lets the session get cleared and the
        // redirect happen; the only cost is Keycloak's logout URL loses `id_token_hint`.
        let resolvedIdToken = idToken;

        if (resolvedIdToken === undefined) {
            try {
                resolvedIdToken = browserSessionStore.getItem('id_token') as string;
            } catch (error) {
                console.error('Failed to read id_token during logout - proceeding without it', error);
                resolvedIdToken = '';
            }
        }

        const isSso = browserSessionStore.getItem('is_sso') === 'true';
        browserSessionStore.removeAllSessionStorage();

        if (isSso) {
            AuthService.history.replace('/ssoLogout');
        } else {
            AuthService.getAuthConfig()
                .then(config => {
                    const logoutUrl = isAdmin ? config.AUTH_ADMIN_LOGOUT_URL : config.AUTH_CLIENT_LOGOUT_URL;
                    const params = new URLSearchParams(logoutUrl.split('?')[1]);

                    if (params.has('returnTo')) {
                        // INFO: Since auth0 is a hosted service, it is used with https protocol throughout for authorization and token
                        // call. But for returnTo url uses http during development since service provider is running in localhost.
                        params.set('returnTo', (import.meta.env.DEV ?
                            'http://'
                            : encodeURIComponent(config.AUTH_PROTOCOL)) + encodeURIComponent(params.get('returnTo') as string));
                    }

                    if (params.has('post_logout_redirect_uri')) {
                        params.set('post_logout_redirect_uri', encodeURIComponent(config.AUTH_PROTOCOL + params.get('post_logout_redirect_uri')));
                    }

                    if (resolvedIdToken) {
                        params.set('id_token_hint', resolvedIdToken);
                    }

                    const baseUrl = logoutUrl.split('?')[0];
                    let queryString = '';
                    params.forEach((value, key) => {
                        queryString += `${key}=${value}&`;
                    });

                    queryString = queryString.slice(0, -1);
                    window.location.replace(`${config.AUTH_PROTOCOL}${baseUrl}?${queryString}`);
                });
        }
    }

    handleCodeAuthentication(): boolean {
        return true;
    }

    setSessionTokens(authResult: TokenResponseDto, idTokenExpiresAt: number): void {
        const expiresAt = idTokenExpiresAt.toString();
        browserSessionStore.setItem('access_token', authResult.access_token);
        browserSessionStore.setItem('expires_at', expiresAt);
        //`id_token` & `refresh_token` setting order is important - must occur after setting `expires_at`
        browserSessionStore.setItem('id_token', authResult.id_token);

        if (authResult.refresh_token) {
            browserSessionStore.setItem('refresh_token', authResult.refresh_token);
        }
    }

    removeSessionStorage(all: boolean): void {
        // Clear Access Token and ID Token from local storage
        browserSessionStore.removeItems([
            'access_token',
            'id_token',
            'expires_at',
            'browser_refresh',
            'refresher'
        ]);

        if (all) {
            browserSessionStore.removeItem('refresh_token');
        }
    }
}

export default AuthService;